<div class="we-modal">

  <!-- ── Header ── -->
  <div class="we-head">

    <div class="we-head-copy">
      <h2 class="we-title">Send Welcome Email</h2>

      <p class="we-sub">
        <ng-container *ngIf="data?.subjectName; else genericSub">
          To contacts on <strong>{{ data.subjectName }}</strong>
          <mat-checkbox [(ngModel)]="coreIncludeinEmil" color="primary" class="include-email-checkbox"
           (change)="onIncludeEmailChange($event)"> Include in {{coreName}} </mat-checkbox>
          <span *ngIf="data?.subjectMeta"> · {{ data.subjectMeta }}</span>
        </ng-container>
        <ng-template #genericSub>Choose who should receive the material</ng-template>
      </p>
    </div>

    <button type="button" class="we-close" aria-label="Close" (click)="cancel()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- ── Body ── -->
  <div class="we-body">

    <div class="we-compose">

      <!-- Channel -->
      <div class="setup-row">
        <div class="setup-col setup-col--channel">
          <span class="field-label" id="weChannelLabel">Send as</span>
          <div class="seg" role="group" aria-labelledby="weChannelLabel">
            <button type="button" class="seg-btn" [class.is-active]="channel === 'email'"
              [attr.aria-pressed]="channel === 'email'" (click)="setChannel('email')">
              <mat-icon>mail_outline</mat-icon> Email
            </button>
            <button type="button" class="seg-btn" [class.is-active]="channel === 'text'"
              [attr.aria-pressed]="channel === 'text'" (click)="setChannel('text')">
              <mat-icon>chat_bubble_outline</mat-icon> Text
            </button>
          </div>
        </div>

        <!-- Conditions on this record, hover a circle for the full name -->
        <div class="setup-col setup-col--conditions">
          <span class="field-label" id="weConditionsLabel">Conditions</span>

          <div class="cond-chips" role="list" aria-labelledby="weConditionsLabel">
            <mat-spinner *ngIf="conditionsLoading" diameter="18"></mat-spinner>

            <span class="cond-slot" role="listitem"
              *ngFor="let condition of conditions; trackBy: trackByCondition">

              <span class="cond-chip" [matTooltip]="condition.name" matTooltipPosition="above"
                matTooltipClass="cond-tooltip" [attr.aria-label]="condition.name">
                {{ condition.initials }}
              </span>

              <button type="button" class="cond-remove"
                [attr.aria-label]="'Remove ' + condition.name"
                (click)="removeCondition(condition)">
                <mat-icon>close</mat-icon>
              </button>
            </span>

            <button type="button" class="cond-chip cond-add" matTooltip="Add condition"
              matTooltipPosition="above" matTooltipClass="cond-tooltip" aria-label="Add condition"
              (click)="openAddCondition(addConditionTpl)">
              <mat-icon>add</mat-icon>
            </button>

            <!-- Far right of the row: opens the educational-content panel -->
            <button type="button" class="edu-toggle" [class.is-on]="showEducationalPanel"
              matTooltip="Educational content" matTooltipPosition="above"
              matTooltipClass="cond-tooltip" aria-label="Educational content"
              [attr.aria-pressed]="showEducationalPanel" (click)="toggleEducationalPanel()">
              <mat-icon>menu_book</mat-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Selection bar -->
      <div class="sendto-row">
        <span class="sendto-label">Send to</span>
        <span class="count-badge">{{ selectedCount }} of {{ totalContacts }} selected</span>

        <div class="search-box">
          <mat-icon>search</mat-icon>
          <input type="text" class="search-input" placeholder="Search contacts" autocomplete="off"
            [ngModel]="searchTerm" (ngModelChange)="onSearchChange($event)">
        </div>

        <div class="sendto-actions">
          <button type="button" class="btn-link" [disabled]="!contacts.length" (click)="selectAll()">
            Select all
          </button>
          <button type="button" class="btn-link muted" [disabled]="!selectedCount" (click)="clearSelection()">
            Clear
          </button>
        </div>
      </div>

      <!-- Loading / failure / empty states for the contact list -->
      <div class="list-state" *ngIf="loading">
        <mat-spinner diameter="26"></mat-spinner>
        <p>Loading contacts…</p>
      </div>

      <div class="list-state is-error" *ngIf="!loading && loadError">
        <mat-icon>error_outline</mat-icon>
        <p>{{ loadError }}</p>
        <button type="button" class="btn btn-ghost" (click)="retryLoad()">Try again</button>
      </div>

      <div class="list-state" *ngIf="!loading && !loadError && !contacts.length">
        <mat-icon>person_search</mat-icon>
        <p *ngIf="searchTerm">No contacts match &ldquo;{{ searchTerm }}&rdquo;.</p>
        <p *ngIf="!searchTerm">No contacts are linked to this record yet.</p>
      </div>

      <!-- Contact picker -->
      <ul class="contact-list" *ngIf="!loading && !loadError && contacts.length">
        <li class="contact-row" *ngFor="let contact of contacts; trackBy: trackByContact"
          [class.is-selected]="isSelected(contact)" [class.is-unreachable]="!isReachable(contact)"
          role="checkbox" [attr.aria-checked]="isSelected(contact)"
          [attr.aria-disabled]="!isReachable(contact)"
          [attr.tabindex]="isReachable(contact) ? 0 : -1" (click)="toggleContact(contact)"
          (keydown)="onRowKeydown($event, contact)">

          <span class="cbox" [class.is-on]="isSelected(contact)" aria-hidden="true">
            <mat-icon *ngIf="isSelected(contact)">check</mat-icon>
          </span>

          <span class="avatar" aria-hidden="true">{{ initials(contact.name) }}</span>

          <span class="identity">
            <span class="identity-top">
              <span class="c-name">{{ contact.name }}</span>
              <span class="c-badge" *ngIf="contact.badge">{{ contact.badge }}</span>
            </span>
            <span class="c-rel">{{ contact.relationship }}</span>
          </span>

          <span class="c-email" [class.is-missing]="!contact.email" [title]="contact.email || ''">
            {{ contact.email || 'No email' }}
          </span>

          <span class="c-phone" [class.is-missing]="!contact.mobile">
            {{ contact.mobile || 'No mobile' }}
          </span>

          <!-- <span class="prefers-pill">Prefers {{ contact.prefers === 'email' ? 'Email' : 'Text' }}</span> -->
        </li>
      </ul>

      <!-- Why some rows are greyed out -->
      <div class="notice" *ngIf="!loading && unreachableContacts.length">
        <mat-icon>warning_amber</mat-icon>
        <span>
          {{ unreachableNames }} {{ unreachableContacts.length === 1 ? 'has' : 'have' }} no
          {{ missingFieldLabel }} on file and can&rsquo;t be selected. Switch to
          {{ otherChannelLabel }} or add it on their contact card.
        </span>
      </div>

      <!-- Create a contact and attach it to this record -->
      <div class="add-recipient" *ngIf="!loading && !loadError">
        <button type="button" class="btn-link add-toggle" (click)="openPopupAddContact(shareAdd)">
          <mat-icon>{{ showAddRecipient ? 'remove' : 'add' }}</mat-icon>
          {{ showAddRecipient ? 'Cancel' : 'Add another recipient' }}
        </button>

        <div class="add-form" *ngIf="showAddRecipient">
          <div class="add-grid">
            <div class="add-cell">
              <label class="field-label" for="weNewName">Name<span class="req">*</span></label>
              <input id="weNewName" type="text" class="form-control" autocomplete="off"
                placeholder="Full name" [(ngModel)]="newContact.name">
            </div>

            <div class="add-cell">
              <label class="field-label" for="weNewEmail">Email<span class="req">*</span></label>
              <input id="weNewEmail" type="email" class="form-control" autocomplete="off"
                placeholder="name@example.com" [(ngModel)]="newContact.email">
            </div>

            <div class="add-cell">
              <label class="field-label" for="weNewPhone">Phone</label>
              <input id="weNewPhone" type="tel" class="form-control" autocomplete="off"
                placeholder="(555) 000-0000" [(ngModel)]="newContact.phone">
            </div>

            <button type="button" class="btn btn-primary add-submit"
              [disabled]="savingContact || !newContact.name || !newContact.email"
              (click)="addNewContact()">
              {{ savingContact ? 'Adding…' : 'Add' }}
            </button>
          </div>

          <p class="add-hint">The contact is created and attached to this record.</p>
        </div>

        <p class="field-error" *ngIf="manualError">{{ manualError }}</p>
      </div>

      <!-- Personal note -->
      <div class="note-block">
        <div class="note-label-row">
          <label class="field-label" for="weNoteTitle">
            Personal message <span class="label-hint">(optional)</span>
          </label>

         

          <span class="note-settings-label" (click)="openPromptTemplates(promptTemplatesTpl)">
            Select a Template to Write Your Message
          </span>
           <button type="button" class="note-settings" matTooltip="Prompt templates"
            matTooltipPosition="above" matTooltipClass="cond-tooltip"
            aria-label="Prompt templates" (click)="openPromptTemplates(promptTemplatesTpl)">
            <mat-icon>settings</mat-icon>
          </button>
        </div>

        <div class="note-title-row">
          <input id="weNoteTitle" type="text" class="form-control note-title" maxlength="150"
            autocomplete="off" placeholder="Message title" [(ngModel)]="personalNoteTitle" style="width: 200px;" >

          <button type="button" class="btn btn-ghost prepare-btn" 
            matTooltip="Draft a message from the title" matTooltipPosition="above"
            matTooltipClass="cond-tooltip" (click)="prepareMessage()">
            <mat-spinner *ngIf="preparingMessage" diameter="14"></mat-spinner>
            <mat-icon *ngIf="!preparingMessage">auto_awesome</mat-icon>
            {{ preparingMessage ? 'Preparing…' : 'Prepare Message' }}
          </button>
        </div>

        <textarea id="weNote" class="form-control note-input" rows="6"
          placeholder="Add a line of your own…" [(ngModel)]="personalNote"></textarea>
      </div>

      <!-- Attachments, listed once something is picked -->
      <ul class="attach-list" *ngIf="attachments.length">
        <li *ngFor="let file of attachments">
          <mat-icon>insert_drive_file</mat-icon>
          <span class="attach-name">{{ file.name }}</span>
          <button type="button" class="attach-x" [attr.aria-label]="'Remove ' + file.name"
            (click)="removeAttachment(file)">
            <mat-icon>close</mat-icon>
          </button>
        </li>
      </ul>

      <!-- Inline schedule field -->
      <div class="schedule-block" *ngIf="showSchedule">
        <label class="field-label" for="weSchedule">Send at</label>
        <input id="weSchedule" type="datetime-local" class="form-control schedule-input"
          [(ngModel)]="scheduledAt">
      </div>
    </div>

    <!-- ── Educational content, opened from the book button ── -->
    <aside class="we-edu" *ngIf="showEducationalPanel" aria-label="Educational content">

      <div class="edu-head">
        <div class="edu-head-copy">
          <h3 class="edu-title">Educational content</h3>
          <p class="edu-sub">{{ selectedMaterialCount }} of {{ educationalMaterials.length }} selected</p>
        </div>

        <button type="button" class="we-close" aria-label="Close educational content"
          (click)="toggleEducationalPanel()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="edu-actions">
        <button type="button" class="btn-link" *ngIf="educationalMaterials.length"
          (click)="selectAllMaterials()">Select all</button>
        <button type="button" class="btn-link muted" *ngIf="educationalMaterials.length"
          [disabled]="!selectedMaterialCount" (click)="clearMaterialSelection()">Clear</button>

        <!-- Far right of the row: pick a file from My Drive -->
        <button type="button" class="edu-add" matTooltip="Add from My Drive"
          matTooltipPosition="above" matTooltipClass="cond-tooltip" aria-label="Add from My Drive"
          (click)="openMyDrive()">
          <mat-icon>add</mat-icon>
        </button>
      </div>

      <div class="list-state" *ngIf="materialsLoading">
        <mat-spinner diameter="26"></mat-spinner>
        <p>Loading content…</p>
      </div>

      <div class="list-state is-error" *ngIf="!materialsLoading && materialsError">
        <mat-icon>error_outline</mat-icon>
        <p>{{ materialsError }}</p>
        <button type="button" class="btn btn-ghost" (click)="retryMaterials()">Try again</button>
      </div>

      <div class="list-state" *ngIf="!materialsLoading && !materialsError && !educationalMaterials.length">
        <mat-icon>menu_book</mat-icon>
        <p>No educational content is available for this record.</p>
      </div>

      <ul class="edu-list" *ngIf="!materialsLoading && !materialsError && educationalMaterials.length">
        <li class="edu-row" *ngFor="let material of educationalMaterials; trackBy: trackByMaterial"
          [class.is-selected]="material.selected" role="checkbox"
          [attr.aria-checked]="material.selected" tabindex="0"
          (click)="toggleMaterial(material)"
          (keydown.space)="$event.preventDefault(); toggleMaterial(material)"
          (keydown.enter)="toggleMaterial(material)">

          <span class="cbox" [class.is-on]="material.selected" aria-hidden="true">
            <mat-icon *ngIf="material.selected">check</mat-icon>
          </span>

          <!-- Title / description click expands the description; the row itself selects -->
          <span class="edu-copy" [class.is-expandable]="material.description"
            [class.is-expanded]="isMaterialExpanded(material)"
            (click)="toggleMaterialExpand(material, $event)">
            <span class="edu-row-top">
              <span class="edu-name">{{ material.title }}</span>
              <span class="edu-type" *ngIf="material.fileType">{{ material.fileType }}</span>
              <mat-icon class="edu-chevron" *ngIf="material.description" aria-hidden="true">expand_more</mat-icon>
            </span>
            <span class="edu-desc" *ngIf="material.description">{{ material.description }}</span>
          </span>
        </li>
      </ul>
    </aside>

  </div>

  <!-- ── Footer ── -->
  <div class="we-foot">

    <div class="foot-left">
      <!-- the input is driven by the visible button, so it stays off-screen -->
      <input #attachInput type="file" multiple class="file-input" (change)="onAttachmentsPicked($event)">

      <!-- <button type="button" class="foot-action" disabled (click)="attachInput.click()">
        <mat-icon>attach_file</mat-icon> Attach
      </button> -->

      <!-- <button type="button" class="foot-action" disabled [class.is-on]="showSchedule"
        (click)="toggleSchedule()">
        <mat-icon>schedule</mat-icon> Schedule
      </button> -->
    </div>

    <div class="we-foot-actions">
      <button type="button" class="btn btn-ghost" (click)="cancel()">Cancel</button>

      <button type="button" class="btn btn-primary" [disabled]="!canSend" (click)="send()">
        <mat-icon>send</mat-icon>
        {{ sending ? 'Sending…' : (selectedCount ? 'Send to ' + selectedCount : 'Send') }}
      </button>
    </div>
  </div>

</div>


<ng-template #shareAdd>
   
      <h2 mat-dialog-title>
            <span  class="groupIcon"><mat-icon>group_add</mat-icon></span>
             Add {{ContactName}}
               <button mat-icon-button (click)="close()">
                 <mat-icon>close</mat-icon>
               </button>
       </h2>
    
  <mat-dialog-content class="mat-typography">
    
  
    <app-contactlist  [objectList]="contactsList" [isSharedAccessRolePopup]="isSharedAccessRolePopup"
     [isRolePopup]="isRolePopup" (NotifyItemSelected)="contactSelected($event)" (NotifyListfetch)="getContactListCoreImplementation($event)"></app-contactlist>
    
    <ul class="selectedlist">
      <li *ngFor="let user of peoples">
        <div class="userndetail">
            <div class="userpic">
              <img *ngIf="user.img!=''" src="{{user.img}}" alt="">
              <span *ngIf="user.img==''" class="username">
                  {{user.shortName}}
                </span>
            </div>
            <div class="detail">
                <h4>{{user.name}}</h4>
                <p>{{user.email}}</p>
            </div>
            <div class="opt">
                <button mat-icon-button><mat-icon (click)="removeItem(user)">cancel</mat-icon></button>
            </div>
        </div>
      </li>
    </ul>
          

  </mat-dialog-content>
  <mat-dialog-actions >
    <button mat-button (click)="close()">Cancel</button>
    <button mat-flat-button color="primary" [disabled]=!showAddButton (click)="addUpdateCoreContact()">Done</button>
  </mat-dialog-actions>
</ng-template>

 <app-addnew  *ngIf="showAddContact" (closeAdd)="getClose($event)" [name]="searchName" (contactSaved)="getNormalAndSharedContacts()"
    [coreID]="coreID" [isShared]="isShare" [mainModuleName]='mainModuleName' [modulename]='Module' [ngClass]="{'open': showAddContact}" >
  </app-addnew>

<!-- Stored prompts. Picking one redrafts the note straight away; the form at
     the foot stores a new prompt against this office. -->
<ng-template #promptTemplatesTpl>
  <h2 mat-dialog-title>
    <span class="groupIcon"><mat-icon>auto_awesome</mat-icon></span>
    Prompt templates
    <button mat-icon-button (click)="closePromptTemplates()">
      <mat-icon>close</mat-icon>
    </button>
  </h2>

  <mat-dialog-content class="mat-typography">

    <div class="list-state" *ngIf="promptTemplatesLoading">
      <mat-spinner diameter="26"></mat-spinner>
      <p>Loading prompts&hellip;</p>
    </div>

    <div class="list-state is-error" *ngIf="!promptTemplatesLoading && promptTemplatesError">
      <mat-icon>error_outline</mat-icon>
      <p>{{ promptTemplatesError }}</p>
      <button type="button" class="btn btn-ghost" (click)="retryPromptTemplates()">Try again</button>
    </div>

    <div class="list-state"
      *ngIf="!promptTemplatesLoading && !promptTemplatesError && !promptTemplates.length">
      <mat-icon>chat_bubble_outline</mat-icon>
      <p>No prompts saved yet.</p>
    </div>

    <ul class="prompt-list"
      *ngIf="!promptTemplatesLoading && !promptTemplatesError && promptTemplates.length">
      <li class="prompt-row" *ngFor="let row of promptTemplates; trackBy: trackByPromptTemplate">
        <span class="prompt-copy">
          <span class="prompt-level" [attr.data-level]="row.level">
            {{ promptLevelLabel(row.level) }}
          </span>
          <span class="prompt-text">{{ row.prompt }}</span>
        </span>

        <button type="button" class="btn btn-primary prompt-use"
          [disabled]="preparingMessage" (click)="usePromptTemplate(row)">
          <mat-icon>auto_awesome</mat-icon> Use
        </button>
      </li>
    </ul>

    <div class="prompt-add">
      <button type="button" class="btn-link add-toggle" (click)="toggleAddPromptTemplate()">
        <mat-icon>{{ showAddPromptTemplate ? 'remove' : 'add' }}</mat-icon>
        {{ showAddPromptTemplate ? 'Cancel' : 'Add new prompt' }}
      </button>

      <div class="add-form" *ngIf="showAddPromptTemplate">
        <textarea class="form-control" rows="3" maxlength="1000"
          placeholder="Describe the message this prompt should draft&hellip;"
          [(ngModel)]="newPromptTemplate"></textarea>

        <button type="button" class="btn btn-primary" [disabled]="!canSavePromptTemplate"
          (click)="addPromptTemplate()">
          {{ savingPromptTemplate ? 'Saving&hellip;' : 'Save prompt' }}
        </button>
      </div>
    </div>
  </mat-dialog-content>
</ng-template>


<!-- Add a condition to the chip row. Local to this dialog; nothing is posted. -->
<ng-template #addConditionTpl>
  <h2 mat-dialog-title>
    <span class="groupIcon"><mat-icon>medical_information</mat-icon></span>
    Add condition
    <button mat-icon-button (click)="closeAddCondition()">
      <mat-icon>close</mat-icon>
    </button>
  </h2>

  <mat-dialog-content class="mat-typography">
    <div class="cond-form">
      <div class="cond-cell">
        <label class="field-label" for="weCondDisplay">Display<span class="req">*</span></label>
        <input id="weCondDisplay" type="text" class="form-control" autocomplete="off"
          placeholder="e.g. Dermatomyositis" [(ngModel)]="newCondition.display">
      </div>

      <div class="cond-cell">
        <label class="field-label" for="weCondCode">Code</label>
        <input id="weCondCode" type="text" class="form-control" autocomplete="off"
          placeholder="e.g. M33.90" [(ngModel)]="newCondition.code">
      </div>

      <p class="field-error" *ngIf="conditionError">{{ conditionError }}</p>
    </div>
  </mat-dialog-content>

  <mat-dialog-actions>
    <button mat-button (click)="closeAddCondition()">Cancel</button>
    <button mat-flat-button color="primary" [disabled]="!newCondition.display"
      (click)="addCondition()">Add</button>
  </mat-dialog-actions>
</ng-template>
